/**
 * toolRegistry.js — the catalog of actions the LiTeCAB Copilot can take.
 *
 * Each tool is { name, description, kind, params, run(args) }:
 *   - kind 'read'  → returns data, no state change (runs silently)
 *   - kind 'write' → mutates the project (snapshotted for undo by agentLoop)
 *   - kind 'ui'    → navigation / onboarding highlight
 *
 * Tools read live state via getState() and mutate ONLY through callAction() on
 * the actionBridge — they never touch React directly. Composite edits (e.g.
 * editing one LCI input) are computed here from the current project and pushed
 * through the bridge's primitive setters (updateProcess / updateProjectTable).
 */

import ipc from '../ipc';
import { getState, callAction } from './actionBridge';
import { summarizeState } from './agentPrompts';
import { callGemini, SYSTEM_PROMPT, parseTemplateResponse } from '../geminiService';
import { getActiveImpactSource, hasImpactScores, isCostOnly, setImpactLink, setActiveImpactSource, impactSourceCoverage, impactSourceLabel, ECOINVENT_SOURCE, GREET_SOURCE } from '../impactSource';
import { describeCostProvenance, readCostProvenance, withCost } from '../costProvenance';
import { searchPrice, sourcedCost, describePriceResult } from '../priceSearch';
import { searchForInput } from '../traciAutoLinker';
import {
  getDesignParameters,
  resolveParamValues,
  computeDerived,
  isInventoryInSync,
  applyDesignParameters,
} from '../../utils/designParameters';
import { BUILTIN_TEMPLATES, BATPAC_TEMPLATES, findBuiltinTemplate } from '../templateCatalog';
import {
  buildConfigurationProject,
  summarizeProject,
  materialParamKeys,
  templateConfigurations,
  percentChange,
} from '../batpacDesign';
import { buildProjectFromTemplate } from '../templateProject';
import { freeProjectName } from '../projectIdentity';

// Built-in templates shipped in data/templates/ (ids are the filenames passed to load-template).
const BUILTIN_TEMPLATE_LIST = BUILTIN_TEMPLATES.map((t) => ({
  id: t.template, name: t.name, chemistry: t.chemistry, category: t.category,
}));

// -- BatPaC process templates -------------------------------------------------

const round = (v, digits) => (Number.isFinite(v) ? Number(v.toFixed(digits)) : null);

function batpacSummary(summary, baseline) {
  if (!summary) return null;
  const out = {
    wh_per_kg: round(summary.whPerKg, 1),
    kg_co2e_per_kwh: round(summary.carbon, 2),
    usd_per_kwh: round(summary.cost, 2),
  };
  if (baseline) {
    out.change_vs_baseline_pct = {
      wh_per_kg: round(percentChange(summary.whPerKg, baseline.whPerKg), 1),
      kg_co2e_per_kwh: round(percentChange(summary.carbon, baseline.carbon), 1),
      usd_per_kwh: round(percentChange(summary.cost, baseline.cost), 1),
    };
  }
  return out;
}

/**
 * Load a BatPaC template and check the user's cathode values against its
 * sliders. Returns { entry, template, block, material } or { error }.
 */
async function loadBatpac(chemistry, material = {}) {
  const key = String(chemistry || '').toLowerCase().replace(/^batpac[_/]/, '').replace(/\.json$/, '');
  const entry = BATPAC_TEMPLATES.find((t) => t.key === key || t.label.toLowerCase() === key);
  if (!entry) return { error: `Unknown chemistry "${chemistry}". Options: ${BATPAC_TEMPLATES.map((t) => t.key).join(', ')}.` };
  let template;
  try { template = await ipc.invoke('load-template', entry.template, { builtin: true }); }
  catch (e) { return { error: `Failed to load the ${entry.label} process template: ${e.message}` }; }
  const block = getDesignParameters(template);
  const allowed = materialParamKeys(block);
  const clean = {};
  for (const [k, raw] of Object.entries(material || {})) {
    const def = block.params[k];
    if (!allowed.includes(k)) return { error: `Unknown cathode value "${k}". Valid keys: ${allowed.join(', ')}.` };
    const v = Number(raw);
    if (!Number.isFinite(v) || v < def.min || v > def.max) {
      return { error: `${def.label} must be between ${def.min} and ${def.max} ${def.unit}.` };
    }
    clean[k] = v;
  }
  return { entry, template, block, material: clean };
}

const num = (v, dflt = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : dflt;
};

const currentProject = () => getState().project;

// Four significant figures keeps design observations readable and token-cheap.
const sig = (v) => (Number.isFinite(v) ? Number(v.toPrecision(4)) : null);

/** The project's design block with its current values and derived outputs, or null. */
function readDesign(project) {
  const block = getDesignParameters(project);
  if (!block) return null;
  const values = resolveParamValues(block, project);
  const { values: derived } = computeDerived(block, values);
  return {
    block,
    values,
    outputs: block.display.map(({ key, label, unit }) => ({ label: label || key, value: sig(derived[key]), unit: unit || '' })),
    inSync: isInventoryInSync(project, block, derived),
  };
}

const TOOLS = [
  // ---------------------------------------------------------------- reads ---
  {
    name: 'get_project_summary',
    kind: 'read',
    description: 'Get a compact summary of the current project, processes, and whether analysis has been run.',
    params: {},
    run: () => summarizeState(getState()),
  },
  {
    name: 'get_process',
    kind: 'read',
    description: 'Get full detail for one process, including its inventory inputs (with indexes) and outputs.',
    params: { processId: 'process ID, e.g. "P002"' },
    run: ({ processId }) => {
      const proj = currentProject();
      const p = proj?.processes?.[processId];
      if (!p) return { error: `No process "${processId}". Known IDs: ${Object.keys(proj?.processes || {}).join(', ') || '(none)'}` };
      const impactSource = getActiveImpactSource(proj);
      return {
        id: p.id,
        name: p.name,
        process_type: p.process_type,
        equipment: p.equipment,
        parameters: p.parameters,
        description: p.description,
        next_process_ids: p.next_process_ids || [],
        outputs: (p.products || p.output_materials || []).map((o, i) => ({
          index: i, name: o.material || o.name, quantity: o.quantity, unit: o.unit, price_per_unit: o.price_per_unit ?? null,
        })),
        lci_inputs: (p.lci_inputs || []).map((inp, i) => ({
          index: i, name: inp.name, quantity: inp.quantity, unit: inp.unit, cost_per_unit: inp.cost_per_unit ?? null,
          cost_confidence: readCostProvenance(inp).confidence,
          linked: hasImpactScores(inp, impactSource),
          ...(isCostOnly(inp) ? { price_only: true } : {}),
          ...(inp.cost_in ? { priced_in: inp.cost_in } : {}),
        })),
      };
    },
  },
  {
    name: 'get_analysis_results',
    kind: 'read',
    description: 'Get the latest LCA/TEA/circularity results, including the top cost-driver processes.',
    params: {},
    run: () => {
      const { analysisResults } = getState();
      const { lca, tea, circularity } = analysisResults || {};
      const out = {};
      if (lca?.success) out.lca = { totals: lca.totals, climate_change_kg_co2e: lca.totals?.climate_change ?? null };
      if (tea?.success) {
        const pb = tea.process_breakdown || {};
        out.tea = {
          total_cost: tea.total_cost,
          total_revenue: tea.total_revenue,
          total_profit: tea.total_profit,
          cost_unit: tea.cost_unit,
          top_cost_processes: Object.values(pb)
            .map((p) => ({ name: p.process_name, cost: p.total_cost }))
            .sort((a, b) => b.cost - a.cost)
            .slice(0, 3),
        };
        if (tea.cost_provenance) {
          out.tea.cited_cost_share = sig(tea.cost_provenance.cited_share);
          out.tea.cost_provenance = describeCostProvenance(tea.cost_provenance);
        }
      }
      if (circularity?.success || circularity?.summary) out.circularity = circularity.summary || circularity;
      if (!out.lca && !out.tea && !out.circularity) return { note: 'No completed analysis yet — run run_all (or run_tea / run_lca) first.' };
      return out;
    },
  },
  {
    name: 'search_lci_database',
    kind: 'read',
    description: 'Search the impact lookup of the project\'s active dataset (ecoinvent or GREET) for activities matching a material/energy name (for linking inputs).',
    params: { query: 'material or energy name', maxResults: 'optional, default 8' },
    run: async ({ query, maxResults = 8 }) => {
      if (!query) return { error: 'query is required' };
      const source = getActiveImpactSource(currentProject());
      const resp = await ipc.invoke('search-traci-lookup', query, maxResults, undefined, source);
      return {
        query,
        dataset: source,
        results: (resp?.results || []).map((r) => ({
          activity_name: r.activity_name, reference_product: r.reference_product, location: r.location,
        })),
      };
    },
  },
  {
    name: 'search_cost_database',
    kind: 'read',
    description: 'Search the curated cost database for a material/energy price.',
    params: { query: 'material or energy name', maxResults: 'optional, default 8' },
    run: async ({ query, maxResults = 8 }) => {
      if (!query) return { error: 'query is required' };
      const resp = await ipc.invoke('search-cost-database', query, maxResults);
      return { query, results: resp?.results || resp || [] };
    },
  },
  {
    name: 'list_templates',
    kind: 'read',
    description: 'List the built-in templates and any user-saved templates available to start a project from.',
    params: {},
    run: async () => {
      let user = [];
      try { user = await ipc.invoke('list-user-templates'); } catch (_) { /* ignore */ }
      return { builtin: BUILTIN_TEMPLATE_LIST, user: user || [] };
    },
  },

  // -------------------------------------------------------- project setup ---
  {
    name: 'create_project_from_template',
    kind: 'write',
    description: 'Create and open a new project from a built-in template (use list_templates for IDs).',
    params: { templateId: 'template filename, e.g. "nmc811_cell_manufacturing.json"', name: 'optional project name' },
    // `projectId` is internal (the walkthrough's demo): not offered to the model.
    run: async ({ templateId, name, projectId }) => {
      if (!templateId) return { error: `templateId required. Options: ${BUILTIN_TEMPLATE_LIST.map((t) => t.id).join(', ')}` };
      let template;
      try { template = await ipc.invoke('load-template', templateId); } catch (e) { return { error: `Failed to load template: ${e.message}` }; }
      if (!template?.processes) return { error: `Template "${templateId}" not found or has no processes.` };
      const builtin = findBuiltinTemplate(templateId);
      const baseName = name || template.project_name || builtin?.name || templateId.replace(/\.json$/, '');
      // A new project takes a name no saved project uses; a fixed id replaces its
      // own earlier copy, so it keeps the name it was given.
      const projName = projectId ? baseName : await freeProjectName(baseName);
      const project = buildProjectFromTemplate(template, {
        name: projName,
        file: builtin?.template || null,
        chemistry: builtin?.chemistry,
        category: builtin?.category,
        projectId,
      });
      await callAction('loadProject', { project, source: 'template' });
      return { ok: true, project: projName, processes: Object.keys(project.processes).length };
    },
  },
  {
    name: 'list_batpac_chemistries',
    kind: 'read',
    description: 'List the cathodes the "Design with BatPaC" screen offers: one BatPaC process template each (GREET 2025 scores), with its family, BatPaC\'s cathode capacity, voltage and density, and any stand-in (a cathode scored as another\'s powder because no dataset has it).',
    params: {},
    run: async () => {
      const out = [];
      for (const t of BATPAC_TEMPLATES) {
        let defaults = null;
        try {
          const block = getDesignParameters(await ipc.invoke('load-template', t.template, { builtin: true }));
          defaults = Object.fromEntries(materialParamKeys(block).map((k) => [k, block.params[k].default]));
        } catch (_) { /* listed without defaults */ }
        out.push({ chemistry: t.key, label: t.label, family: t.family, stand_in: t.standIn || null, batpac_values: defaults });
      }
      return { chemistries: out, configurations: ['energy', 'thick', 'power (not for LMR)'] };
    },
  },
  {
    name: 'preview_batpac_designs',
    kind: 'read',
    description: 'Wh/kg, kg CO2e per kWh and $ per kWh for each BatPaC cell design (energy, thick electrodes, power) of one cathode, optionally with the user\'s own cathode values, against that cathode\'s energy cell at BatPaC\'s values. Creates nothing.',
    params: {
      chemistry: 'cathode key from list_batpac_chemistries, e.g. "nmc811"',
      material: 'optional { q_pos_mah_g, ocv_v, rho_am_pos }: the user\'s cathode capacity (mAh/g), average voltage (V), crystal density (g/cm3)',
    },
    run: async ({ chemistry, material } = {}) => {
      const loaded = await loadBatpac(chemistry, material);
      if (loaded.error) return loaded;
      const { entry, template } = loaded;
      const baseline = summarizeProject(buildConfigurationProject(template));
      return {
        chemistry: entry.label,
        stand_in: template.batpac?.stand_in || null,
        dataset: impactSourceLabel(template.impact_dataset),
        baseline: { name: `${entry.label} energy cell, BatPaC's values`, ...batpacSummary(baseline) },
        designs: templateConfigurations(template).map((config) => ({
          configuration: config.id,
          name: config.label,
          description: config.description,
          ...batpacSummary(summarizeProject(buildConfigurationProject(template, config, loaded.material)), baseline),
        })),
      };
    },
  },
  {
    name: 'design_with_batpac',
    kind: 'write',
    description: 'Create and open a project from a BatPaC process template: one cathode, one cell design, optionally the user\'s own cathode values. The same as picking it on the "Design with BatPaC" screen. The project is scored on GREET 2025.',
    params: {
      chemistry: 'cathode key from list_batpac_chemistries, e.g. "lfp"',
      configuration: 'optional "energy" (default), "thick" or "power"',
      material: 'optional { q_pos_mah_g, ocv_v, rho_am_pos }',
      name: 'optional project name',
    },
    run: async ({ chemistry, configuration = 'energy', material, name } = {}) => {
      const loaded = await loadBatpac(chemistry, material);
      if (loaded.error) return loaded;
      const { entry, template } = loaded;
      const configs = templateConfigurations(template);
      const config = configs.find((c) => c.id === configuration);
      if (!config) return { error: `Unknown configuration "${configuration}" for ${entry.label}. Options: ${configs.map((c) => c.id).join(', ')}.` };
      const own = Object.keys(loaded.material).length > 0;
      const projName = await freeProjectName(name || `${entry.label} ${config.label.toLowerCase()}${own ? ', my cathode' : ''}`);
      const project = buildConfigurationProject(template, config, loaded.material, { name: projName, file: entry.template });
      await callAction('loadProject', { project, source: 'template' });
      return {
        ok: true,
        project: projName,
        stand_in: template.batpac?.stand_in || null,
        ...batpacSummary(summarizeProject(project)),
        note: 'Results are per kWh on GREET 2025; run_all to show them on screen.',
      };
    },
  },
  {
    name: 'create_project_from_description',
    kind: 'write',
    description: 'Generate a new project template from a free-text process description and open it. Inputs start unlinked (no TRACI scores).',
    params: { description: 'process description', name: 'optional project name' },
    run: async ({ description, name }) => {
      if (!description) return { error: 'description is required' };
      let raw;
      try { raw = await callGemini([{ role: 'user', text: description }], { systemPrompt: SYSTEM_PROMPT }); }
      catch (e) { return { error: `Generation failed: ${e.message}` }; }
      let template;
      try { template = parseTemplateResponse(raw); } catch (e) { return { error: `Generated output was not a valid template: ${e.message}` }; }
      const projName = await freeProjectName(name || template.project_name || 'AI Project');
      const project = buildProjectFromTemplate(template, {
        name: projName,
        description: template.description || 'AI-generated template',
      });
      await callAction('loadProject', { project, source: 'template' });
      return { ok: true, project: projName, processes: Object.keys(project.processes).length, note: 'Inputs are unlinked — link them (set_input_link) before LCA; TEA uses baked cost_per_unit.' };
    },
  },

  // ------------------------------------------------------- process edits ---
  {
    name: 'add_process',
    kind: 'write',
    description: 'Add a new process, placed in the flow. Use `after` (upstream step) and/or `before` (downstream step): `after` alone appends, `after`+`before` inserts between them, `before` alone makes it feed into that step. Goes through the same add path as the UI.',
    params: { name: 'process name', after: 'optional upstream process ID', before: 'optional downstream process ID', process_type: 'optional', parameters: 'optional {duration_hours, temperature_c, pressure_atm}', description: 'optional' },
    run: async ({ name, after, before, process_type, parameters, description }) => {
      const proj = currentProject();
      if (!proj) return { error: 'No project loaded.' };
      const res = await callAction('addProcess', { afterId: after, beforeId: before, name, process_type, parameters, description });
      if (!res?.id) return { error: 'Could not add the process.' };
      return { ok: true, id: res.id, name: res.name };
    },
  },
  {
    name: 'update_process',
    kind: 'write',
    description: 'Update a process’s name, type, equipment, description, or parameters (duration/temperature/pressure).',
    params: { processId: 'process ID', name: 'optional', process_type: 'optional', equipment: 'optional', description: 'optional', parameters: 'optional object merged into parameters' },
    run: async ({ processId, name, process_type, equipment, description, parameters }) => {
      const proj = currentProject();
      const p = proj?.processes?.[processId];
      if (!p) return { error: `No process "${processId}".` };
      const updated = { ...p };
      if (name) updated.name = String(name).replace(/\s+/g, '_');
      if (process_type) updated.process_type = process_type;
      if (equipment !== undefined) updated.equipment = equipment;
      if (description !== undefined) updated.description = description;
      if (parameters && typeof parameters === 'object') updated.parameters = { ...updated.parameters, ...parameters };
      await callAction('updateProjectTable', { project: { ...proj, processes: { ...proj.processes, [processId]: updated } } });
      return { ok: true, id: processId };
    },
  },
  {
    name: 'rename_process',
    kind: 'write',
    description: 'Rename a process.',
    params: { processId: 'process ID', name: 'new name' },
    run: async ({ processId, name }) => {
      if (!name) return { error: 'name is required' };
      await callAction('renameProcess', { processId, name });
      return { ok: true, id: processId, name };
    },
  },
  {
    name: 'delete_process',
    kind: 'write',
    description: 'Delete a process and remove references to it from other processes’ flow.',
    params: { processId: 'process ID' },
    run: async ({ processId }) => {
      const proj = currentProject();
      if (!proj?.processes?.[processId]) return { error: `No process "${processId}".` };
      const processes = {};
      Object.entries(proj.processes).forEach(([id, p]) => {
        if (id === processId) return;
        processes[id] = { ...p, next_process_ids: (p.next_process_ids || []).filter((n) => n !== processId) };
      });
      await callAction('updateProjectTable', { project: { ...proj, processes } });
      return { ok: true, removed: processId, remaining: Object.keys(processes).length };
    },
  },

  // --------------------------------------------------------- inventory ---
  {
    name: 'add_lci_input',
    kind: 'write',
    description: 'Add an inventory input (material/energy) to a process.',
    params: { processId: 'process ID', name: 'input name', quantity: 'number', unit: 'e.g. kg, kWh', cost_per_unit: 'optional number', cost_unit: 'optional e.g. USD/kg' },
    run: async ({ processId, name, quantity, unit, cost_per_unit, cost_unit }) => {
      const proj = currentProject();
      const p = proj?.processes?.[processId];
      if (!p) return { error: `No process "${processId}".` };
      if (!name) return { error: 'name is required' };
      const inputs = [...(p.lci_inputs || []), {
        name, quantity: num(quantity), unit: unit || 'kg',
        cost_per_unit: cost_per_unit != null ? num(cost_per_unit) : null,
        cost_unit: cost_unit || null,
      }];
      await callAction('updateProcess', { processId, updates: { lci_inputs: inputs } });
      return { ok: true, processId, index: inputs.length - 1, input_count: inputs.length };
    },
  },
  {
    name: 'update_lci_input',
    kind: 'write',
    description: 'Update an existing inventory input by index (name, quantity, unit, cost).',
    params: { processId: 'process ID', index: 'input index from get_process', name: 'optional', quantity: 'optional number', unit: 'optional', cost_per_unit: 'optional number', cost_unit: 'optional' },
    run: async ({ processId, index, name, quantity, unit, cost_per_unit, cost_unit }) => {
      const proj = currentProject();
      const p = proj?.processes?.[processId];
      if (!p) return { error: `No process "${processId}".` };
      const inputs = [...(p.lci_inputs || [])];
      if (!inputs[index]) return { error: `No input at index ${index} in ${processId}.` };
      let next = { ...inputs[index] };
      if (name !== undefined) next.name = name;
      if (quantity !== undefined) next.quantity = num(quantity);
      // A new price, or the same number per a different unit, drops the old citation.
      if (unit !== undefined && unit !== next.unit) next = withCost({ ...next, unit }, next.cost_per_unit);
      if (cost_per_unit !== undefined && num(cost_per_unit) !== next.cost_per_unit) {
        next = withCost(next, num(cost_per_unit));
      }
      if (cost_unit !== undefined) next.cost_unit = cost_unit;
      inputs[index] = next;
      await callAction('updateProcess', { processId, updates: { lci_inputs: inputs } });
      return { ok: true, processId, index };
    },
  },
  {
    name: 'find_price',
    kind: 'write',
    description: 'Look up a current market price for one inventory input on the web, from approved price reporters (takes 30-90 s). The price is applied only if every check passes: approved site, number printed on the page with its currency and unit, dated within 18 months, grade matches. Otherwise nothing changes and the best candidate is returned with its reasons.',
    params: { processId: 'process ID', index: 'input index from get_process', spec: 'optional grade/form the price must match, e.g. "battery grade, >=99.5%"' },
    run: async ({ processId, index, spec }) => {
      const proj = currentProject();
      const p = proj?.processes?.[processId];
      if (!p) return { error: `No process "${processId}".` };
      const input = p.lci_inputs?.[index];
      if (!input) return { error: `No input at index ${index} in ${processId}.` };
      let result;
      try {
        result = await searchPrice({
          material: input.name,
          spec: spec || '',
          unit: input.unit || 'kg',
          context: `An input to the "${p.name || processId}" process${proj.project_name ? ` in ${proj.project_name}` : ''}.`,
        });
      } catch (err) {
        return { error: `Price search failed: ${err.message}` };
      }
      const found = describePriceResult(result);
      const update = sourcedCost(result);
      if (!update) return { applied: false, ...found };
      // The search takes a minute; apply to the input as it is now, and only if it is still the same one.
      const inputs = [...(currentProject()?.processes?.[processId]?.lci_inputs || [])];
      if (inputs[index]?.name !== input.name) return { applied: false, error: 'The input changed while the search ran, so the price was not applied.', ...found };
      inputs[index] = withCost(inputs[index], update.cost_per_unit, update);
      await callAction('updateProcess', { processId, updates: { lci_inputs: inputs } });
      return { applied: true, previous_cost_per_unit: input.cost_per_unit ?? null, ...found };
    },
  },
  {
    name: 'remove_lci_input',
    kind: 'write',
    description: 'Remove an inventory input from a process by index.',
    params: { processId: 'process ID', index: 'input index' },
    run: async ({ processId, index }) => {
      const proj = currentProject();
      const p = proj?.processes?.[processId];
      if (!p) return { error: `No process "${processId}".` };
      const inputs = [...(p.lci_inputs || [])];
      if (!inputs[index]) return { error: `No input at index ${index}.` };
      const removed = inputs.splice(index, 1)[0];
      await callAction('updateProcess', { processId, updates: { lci_inputs: inputs } });
      return { ok: true, removed: removed?.name, input_count: inputs.length };
    },
  },
  {
    name: 'set_input_link',
    kind: 'write',
    description: 'Link an inventory input to the best-matching database activity so it gets TRACI scores (enables LCA).',
    params: { processId: 'process ID', index: 'input index', query: 'optional search term (defaults to the input name)' },
    run: async ({ processId, index, query }) => {
      const proj = currentProject();
      const p = proj?.processes?.[processId];
      if (!p) return { error: `No process "${processId}".` };
      const inputs = [...(p.lci_inputs || [])];
      const inp = inputs[index];
      if (!inp) return { error: `No input at index ${index}.` };
      const q = query || inp.activity_name || inp.name;
      // Prefers the input's own location, then the RoW/GLO market ecoinvent falls back to.
      // A custom query may be relinking to another product, so only the location carries over.
      const impactSource = getActiveImpactSource(proj);
      const [top] = await searchForInput({
        activityName: q, referenceProduct: query ? undefined : inp.reference_product, location: inp.location, impactSource,
      }, 5);
      if (!top) return { error: `No ${impactSource} match for "${q}".` };
      inputs[index] = setImpactLink(inp, impactSource, {
        scores: top.traci_scores,
        activity_name: top.activity_name,
        reference_product: top.reference_product,
        location: top.location,
      });
      await callAction('updateProcess', { processId, updates: { lci_inputs: inputs } });
      return {
        ok: true, linked_to: top.activity_name, reference_product: top.reference_product, location: top.location,
        ...(inp.location && inp.location !== top.location ? { requested_location: inp.location } : {}),
      };
    },
  },
  {
    name: 'set_output_price',
    kind: 'write',
    description: 'Set (or clear) the selling price of a process output, which drives revenue/profit in TEA.',
    params: { processId: 'process ID', index: 'output index', price: 'price per unit (0 to clear)' },
    run: async ({ processId, index, price }) => {
      const proj = currentProject();
      const p = proj?.processes?.[processId];
      if (!p) return { error: `No process "${processId}".` };
      const field = p.products ? 'products' : 'output_materials';
      const list = [...(p[field] || [])];
      if (!list[index]) return { error: `No output at index ${index}.` };
      const v = Number(price);
      const next = { ...list[index] };
      if (Number.isFinite(v) && v > 0) next.price_per_unit = v; else delete next.price_per_unit;
      list[index] = next;
      await callAction('updateProcess', { processId, updates: { [field]: list } });
      return { ok: true, price: next.price_per_unit ?? null };
    },
  },
  {
    name: 'set_scaling_factor',
    kind: 'write',
    description: 'Set the project scaling factor (multiplies all quantities, impacts, and costs).',
    params: { value: 'number, e.g. 1000' },
    run: async ({ value }) => callAction('setScalingFactor', { value }),
  },
  {
    name: 'set_fu_basis',
    kind: 'write',
    description: "Set the functional-unit basis: 'energy' normalizes results per kWh (needs an energy density), 'mass' shows per kg.",
    params: { basis: "one of: energy, mass" },
    run: async ({ basis }) => callAction('setFuBasis', { basis }),
  },
  {
    name: 'set_energy_density',
    kind: 'write',
    description: 'Set the cell energy density in Wh/kg, used to convert the mass functional unit to a per-kWh basis. If the project has a design block, change the design with set_design_parameters instead: it sets the energy density itself.',
    params: { value: 'number Wh/kg, e.g. 250' },
    run: async ({ value }) => callAction('setEnergyDensity', { value }),
  },
  {
    name: 'get_design_parameters',
    kind: 'read',
    description: "Get the project's design block (e.g. the cell templates' electrode design): each parameter with its range and current value, the outputs it derives (including cell Wh/kg), and whether the inventory currently matches the design.",
    params: {},
    run: () => {
      const design = readDesign(currentProject());
      if (!design) return { note: 'This project has no design parameters. Only templates that declare a design block (the four cell templates) have them.' };
      const { block, values, outputs, inSync } = design;
      return {
        title: block.title,
        basis: block.basis || null,
        params: Object.entries(block.params).map(([key, def]) => ({
          key, label: def.label || key, unit: def.unit || '', min: def.min, max: def.max, default: def.default, value: values[key],
        })),
        outputs,
        inventory_matches_design: inSync,
        sources: block.sources,
        notes: block.notes,
      };
    },
  },
  {
    name: 'set_design_parameters',
    kind: 'write',
    description: "Change the design parameters and apply them, the same as moving the sliders and pressing Apply: every mapped material quantity and the cell's energy density are recomputed together. Unlisted parameters keep their current value; pass {} to apply the current design unchanged. Re-run the analysis afterwards.",
    params: { values: 'object of { param_key: number }, keys from get_design_parameters, e.g. {"cathode_thickness_um": 120}' },
    run: async ({ values = {} } = {}) => {
      const proj = currentProject();
      if (!proj) return { error: 'No project loaded.' };
      const block = getDesignParameters(proj);
      if (!block) return { error: 'This project has no design parameters.' };
      if (!values || typeof values !== 'object') return { error: 'values must be an object of { param_key: number }.' };

      const next = resolveParamValues(block, proj);
      for (const [key, raw] of Object.entries(values)) {
        const def = block.params[key];
        if (!def) return { error: `Unknown parameter "${key}". Valid keys: ${Object.keys(block.params).join(', ')}.` };
        const v = Number(raw);
        if (!Number.isFinite(v)) return { error: `${key} must be a number.` };
        if ((Number.isFinite(def.min) && v < def.min) || (Number.isFinite(def.max) && v > def.max)) {
          return { error: `${def.label || key} must be between ${def.min} and ${def.max}${def.unit ? ` ${def.unit}` : ''}; the design equations are not valid outside that range.` };
        }
        next[key] = v;
      }

      const result = applyDesignParameters(proj, block, next);
      await callAction('updateProjectTable', { project: result.project });

      // A process open in Build would otherwise keep showing its old quantities.
      const selectedId = getState().selectedProcess?.id;
      if (selectedId && result.applied.some((m) => m.process_id === selectedId)) {
        await callAction('selectProcess', { processId: selectedId });
      }

      const design = readDesign(result.project);
      return {
        ok: true,
        values: next,
        outputs: design?.outputs || [],
        energy_density_wh_per_kg: result.project.project_info?.energy_density_wh_per_kg ?? null,
        quantities_updated: result.applied.length,
        ...(result.unresolved.length ? { skipped: result.unresolved.map((u) => `${u.process_id}: ${u.reason}`) } : {}),
        ...(result.errors.length ? { formula_errors: result.errors.map((e) => `${e.key}: ${e.message}`) } : {}),
        note: 'Previous results are stale. Run the analysis again before quoting numbers.',
      };
    },
  },
  {
    name: 'set_impact_source',
    kind: 'write',
    description: 'Switch the impact dataset the LCA scores come from: "ecoinvent_3.11" (default) or "greet_2025" (open data). Re-runs the LCA if results exist. Inputs the new dataset cannot cover count as zero; the result lists them.',
    params: { source: 'one of: ecoinvent_3.11, greet_2025' },
    run: async ({ source }) => {
      const proj = currentProject();
      if (!proj) return { error: 'No project loaded.' };
      if (![ECOINVENT_SOURCE, GREET_SOURCE].includes(source)) return { error: `source must be ${ECOINVENT_SOURCE} or ${GREET_SOURCE}` };
      const hadLca = !!getState().analysisResults?.lca;
      await callAction('updateProjectTable', { project: setActiveImpactSource(proj, source) });
      const cov = impactSourceCoverage(currentProject(), source);
      if (hadLca) await callAction('runLCA');
      return {
        ok: true,
        impact_source: source,
        label: impactSourceLabel(source),
        covered: cov.linked,
        total: cov.total,
        not_covered: [...new Set(cov.unlinked)],
        ...(cov.unlinkedCarbonShare !== null
          ? { not_covered_carbon_share: Number(cov.unlinkedCarbonShare.toFixed(4)), share_estimated_with: cov.estimatedWith }
          : {}),
        note: hadLca ? 'LCA re-run on the new dataset. Call get_analysis_results for numbers.' : 'Run run_lca to score the project on this dataset.',
      };
    },
  },
  {
    name: 'set_project_field',
    kind: 'write',
    description: 'Update a project-info field (name, description, chemistry, category, battery_modules, battery_life_extension).',
    params: { field: 'one of: name, description, chemistry, category, battery_modules, battery_life_extension', value: 'new value' },
    run: async ({ field, value }) => {
      const proj = currentProject();
      if (!proj) return { error: 'No project loaded.' };
      const allowed = ['name', 'description', 'chemistry', 'category', 'battery_modules', 'battery_life_extension'];
      if (!allowed.includes(field)) return { error: `field must be one of: ${allowed.join(', ')}` };
      const updated = { ...proj, project_info: { ...proj.project_info, [field]: value } };
      await callAction('updateProjectTable', { project: updated });
      return { ok: true, [field]: value };
    },
  },

  // ----------------------------------------------------------- analysis ---
  { name: 'run_lca', kind: 'write', description: 'Run the LCA (TRACI v2.1) analysis. Requires inputs to be linked.', params: {}, run: async () => { await callAction('runLCA'); return { ok: true, note: 'LCA run. Call get_analysis_results for numbers (lca_run will be false if inputs are unlinked).' }; } },
  { name: 'run_tea', kind: 'write', description: 'Run the techno-economic (cost) analysis.', params: {}, run: async () => { await callAction('runTEA'); return { ok: true, note: 'TEA run. Call get_analysis_results for numbers.' }; } },
  { name: 'run_circularity', kind: 'write', description: 'Run the circularity analysis.', params: {}, run: async () => { await callAction('runCircularity'); return { ok: true, note: 'Circularity run. Call get_analysis_results for numbers.' }; } },
  { name: 'run_all', kind: 'write', description: 'Run LCA, TEA, and circularity together.', params: {}, run: async () => { await callAction('runAll'); return { ok: true, note: 'Ran all analyses. Call get_analysis_results for numbers.' }; } },
  { name: 'save_project', kind: 'write', description: 'Save the current project to disk / storage.', params: {}, run: async () => { await callAction('saveProject'); return { ok: true }; } },
  {
    name: 'export_results',
    kind: 'ui',
    description: 'Export the current LCA/TEA results to an Excel (.xlsx) workbook the user can download. Requires analysis to have been run first.',
    params: {},
    run: async () => {
      const { analysisResults } = getState();
      const r = analysisResults || {};
      if (!(r.lca?.success || r.tea?.success || r.circularity?.success)) {
        return { error: 'No analysis results to export yet — run the analysis first (run_all).' };
      }
      await callAction('exportResults', {});
      return { ok: true, note: 'Results exported to an .xlsx download.' };
    },
  },

  // --------------------------------------------------------- ui / coach ---
  {
    name: 'navigate',
    kind: 'ui',
    description: 'Switch the focus mode and/or select a process so the user sees the relevant view.',
    params: { mode: 'optional focus mode: build | inventory | results | compare', left: 'optional Build sub-view: diagram | list | table', processId: 'optional process to select (opens it in Build)' },
    run: async ({ mode, left, right, processId }) => {
      if (processId !== undefined) await callAction('selectProcess', { processId });
      if (mode || left || right) await callAction('setView', { mode, left, right });
      return { ok: true };
    },
  },
  {
    name: 'highlight',
    kind: 'ui',
    description: 'Spotlight an on-screen element with a tooltip during onboarding. Use known coach anchor IDs.',
    params: { coachId: 'anchor id, e.g. "start-batpac", "btn-run-all"', message: 'short tooltip text' },
    run: async ({ coachId, message }) => {
      try { await callAction('highlight', { coachId, message }); return { ok: true }; }
      catch (_) { return { ok: false, note: 'Highlight overlay unavailable — explain in text instead.' }; }
    },
  },
];

/** Catalog (metadata only) for injection into the system prompt. */
export function getToolCatalog() {
  return TOOLS.map(({ name, description, kind, params }) => ({ name, description, kind, params }));
}

/** Find a tool definition by name. */
export function getTool(name) {
  return TOOLS.find((t) => t.name === name) || null;
}

/** Execute a tool by name, returning a JSON-serializable observation. */
export async function runTool(name, args = {}) {
  const tool = getTool(name);
  if (!tool) return { error: `Unknown tool "${name}".` };
  try {
    return (await tool.run(args || {})) ?? { ok: true };
  } catch (err) {
    return { error: err.message || String(err) };
  }
}

export default { getToolCatalog, getTool, runTool };
